import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Button, Form, Input, Modal, Select, Space, Table, Tag } from 'antd'
import { SearchOutlined } from '@ant-design/icons'

import {
  useAgreementTemplates,
  useCreateAgreementTemplate,
  useDeleteAgreementTemplate,
  useUpdateAgreementTemplate,
} from '../hooks/useAgreementTemplates'
import { AgreementTemplateTypeLabels } from '../services/agreementService'
import { getPagination } from '../components/TablePagination'
import type {
  AgreementTemplate,
  AgreementTemplateQueryParams,
  AgreementTemplateType,
} from '../services/agreementService'

export const Route = createFileRoute('/agreementTemplate')({
  component: AgreementTemplateManagement,
})

function AgreementTemplateManagement() {
  // 从URL初始化搜索参数
  const [searchParams, setSearchParams] =
    useState<AgreementTemplateQueryParams>(() => {
      const search = new URLSearchParams(window.location.search)
      return {
        pageNum: search.get('pageNum') || '1',
        pageSize: search.get('pageSize') || '10',
        type: search.get('type') || undefined,
      }
    })

  // 同步URL参数
  useEffect(() => {
    const params = new URLSearchParams()
    params.set('pageNum', searchParams.pageNum)
    params.set('pageSize', searchParams.pageSize)

    // 添加类型筛选参数
    if (searchParams.type) {
      params.set('type', searchParams.type)
    }

    const newUrl = `${window.location.pathname}?${params.toString()}`
    window.history.replaceState(null, '', newUrl)
  }, [searchParams])

  const { data: templateData, isLoading } = useAgreementTemplates(searchParams)
  const createMutation = useCreateAgreementTemplate()
  const updateMutation = useUpdateAgreementTemplate()
  const deleteMutation = useDeleteAgreementTemplate()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<AgreementTemplate | null>(
    null,
  )
  const [form] = Form.useForm()

  const templates = templateData?.records || []
  const total = templateData?.total || 0
  const pageNum = templateData?.current || 1
  const pageSize = templateData?.size || 10

  const handleAdd = () => {
    setEditingRecord(null)
    form.resetFields()
    setIsModalOpen(true)
  }

  const handleEdit = (record: AgreementTemplate) => {
    setEditingRecord(record)
    form.setFieldsValue(record)
    setIsModalOpen(true)
  }

  const handleDelete = (record: AgreementTemplate) => {
    Modal.confirm({
      title: '删除确认',
      content: `确定要删除模板 ${record.id} 吗？`,
      onOk: () => {
        deleteMutation.mutate(record.id)
      },
    })
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      if (editingRecord) {
        updateMutation.mutate(
          { id: editingRecord.id, template: values },
          {
            onSuccess: () => {
              setIsModalOpen(false)
            },
          },
        )
      } else {
        createMutation.mutate(values, {
          onSuccess: () => {
            setIsModalOpen(false)
          },
        })
      }
    } catch (error) {
      // 验证失败
    }
  }

  // 处理表格分页变化
  const handlePaginationChange = (page: number, pageSize: number) => {
    setSearchParams((prev) => ({
      ...prev,
      pageNum: page.toString(),
      pageSize: pageSize.toString(),
    }))
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      filteredValue: searchParams.type ? [searchParams.type] : undefined,
      // 将筛选 UI 内嵌到列标题中
      filterDropdown: (props: any) => {
        const { setSelectedKeys, selectedKeys, confirm, clearFilters } = props
        return (
          <div style={{ padding: 8 }}>
            <Select
              placeholder="选择协议类型"
              allowClear
              style={{ width: 180, marginBottom: 8, display: 'block' }}
              value={selectedKeys && selectedKeys[0]}
              onChange={(val) => setSelectedKeys(val ? [val] : [])}
            >
              {Object.entries(AgreementTemplateTypeLabels).map(
                ([key, value]) => (
                  <Select.Option key={key} value={key}>
                    {value}
                  </Select.Option>
                ),
              )}
            </Select>
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  const val = selectedKeys && selectedKeys[0]
                  setSearchParams((prev) => ({
                    ...prev,
                    type: val,
                    pageNum: '1',
                  }))
                  confirm()
                }}
              >
                确定
              </Button>
              <Button
                size="small"
                onClick={() => {
                  clearFilters && clearFilters()
                  setSelectedKeys([])
                  setSearchParams((prev) => ({
                    ...prev,
                    type: undefined,
                    pageNum: '1',
                  }))
                  confirm()
                }}
              >
                重置
              </Button>
            </Space>
          </div>
        )
      },
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      render: (type: string) => (
        <Tag color="blue">
          {AgreementTemplateTypeLabels[type as AgreementTemplateType]}
        </Tag>
      ),
      onFilter: (value: any, record: AgreementTemplate) =>
        record.type === value,
    },
    {
      title: '内容摘要',
      dataIndex: 'content',
      key: 'content',
      render: (content: string) =>
        content.length > 30 ? `${content.substring(0, 30)}...` : content,
    },
    {
      title: '模板 ID',
      dataIndex: 'link',
      key: 'link',
      render: (link: string) => <p>{link}</p>,
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_: any, record: AgreementTemplate) => (
        <Space size="small">
          <Button size="small" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button size="small" danger onClick={() => handleDelete(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">学员协议管理</h2>
        <div className="flex items-center space-x-4">
          <Button type="primary" onClick={handleAdd}>
            新建模板
          </Button>
        </div>
      </div>
      <Table
        bordered
        dataSource={templates}
        columns={columns}
        rowKey="id"
        pagination={getPagination(
          pageNum,
          pageSize,
          total,
          handlePaginationChange,
        )}
        loading={isLoading}
      />
      <Modal
        title={editingRecord ? '编辑模板' : '新建模板'}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={() => setIsModalOpen(false)}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="type"
            label="类型"
            rules={[{ required: true, message: '请选择类型' }]}
          >
            <Select>
              {Object.entries(AgreementTemplateTypeLabels).map(
                ([key, value]) => (
                  <Select.Option key={key} value={key}>
                    {value}
                  </Select.Option>
                ),
              )}
            </Select>
          </Form.Item>
          <Form.Item
            name="content"
            label="内容"
            rules={[{ required: true, message: '请输入内容' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item
            name="link"
            label="模板 ID"
            rules={[{ required: true, message: '请输入模板 ID' }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
